import { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useActivateVoucher } from '@/api';
import { ProfileContainer } from '@/module/profile/share/UserProfile/ProfileWidget';
import { alertDialog } from '@/ui_v3';
import { useVoucherAlertModal } from '../VoucherAlertModal';
import { type VoucherGuideState, type VoucherItem } from './type';
import { VoucherGuideBar } from './VoucherGuideBar';
import { VoucherTable } from './VoucherTable';
import { VoucherTableSkeleton } from './VoucherTableSkeleton';
export const VoucherStorageSection = ({
  vouchers,
  canUseVouchers,
  isLoading
}: {
  vouchers: VoucherItem[];
  canUseVouchers: boolean;
  isLoading: boolean;
}) => {
  const {
    t
  } = useTranslation(['billing']);
  const {
    mutateAsync: activateVoucher,
    isPending
  } = useActivateVoucher();
  const {
    openActivateConfirmModal
  } = useVoucherAlertModal();
  const [isReloading, setIsReloading] = useState(false);
  const isVoucherActivating = isPending || isReloading;
  const isEmpty = vouchers.length === 0;
  const hasUnusedVouchers = vouchers.some(v => v.status === 'UNUSED');
  const guideState: VoucherGuideState = useMemo(() => {
    if (!canUseVouchers) {
      return 'destructive';
    }
    if (isEmpty || !hasUnusedVouchers) {
      return 'plain';
    }
    return 'available';
  }, [canUseVouchers, isEmpty, hasUnusedVouchers]);
  const handleUseVoucher = async ({
    id,
    name,
    planType
  }: {
    id: string;
    name: string;
    planType: string;
  }) => {
    logger.track('view_voucher_use_modal', {
      voucher_id: id
    });
    const activateConfirmed = await openActivateConfirmModal(name, planType);
    if (!activateConfirmed) {
      return;
    }
    logger.track('click_voucher_use_confirm', {
      voucher_id: id
    });
    try {
      setIsReloading(true);
      await activateVoucher({
        voucherHashId: id
      });
      window.location.reload();
    } catch (error) {
      setIsReloading(false);
      logger.captureError(error, {
        tags: {
          feature: 'voucher',
          action: 'activate'
        },
        extra: {
          voucherHashId: id,
          voucherName: name
        }
      });
      await alertDialog.openAsync({
        header: {
          title: t('voucher.activateFailed.title')
        },
        body: t('voucher.commonError.body')
      });
    }
  };
  return <div className="flex w-full flex-col gap-xs pt-[9px] mobile-lg:pt-lg" data-sentry-component="VoucherStorageSection" data-sentry-source-file="VoucherStorageSection.tsx">
      <div className="flex w-full items-center justify-center px-xs py-none">
        <span className="flex-1 whitespace-pre-wrap text-[15px] font-bold leading-[21px] text-neutral mobile-lg:text-body-lg-bold">
          {t('voucher.storage.title')}
        </span>
      </div>
      <ProfileContainer className="rounded-xl" data-sentry-element="ProfileContainer" data-sentry-source-file="VoucherStorageSection.tsx">
        <div className="flex w-full flex-col gap-md overflow-x-auto px-lg py-lg mobile-lg:px-2xl">
          {isLoading ? <VoucherTableSkeleton /> : <>
              <VoucherGuideBar state={guideState} />
              <VoucherTable vouchers={vouchers} onUseVoucher={handleUseVoucher} isActivating={isVoucherActivating} />
            </>}
        </div>
      </ProfileContainer>
    </div>;
};